import type { DynamicDialogInstance, DynamicDialogOptions } from 'primevue/dynamicdialogoptions';
import type {
    ModalDataForComponent,
    ModalDataMap,
    ModalNames,
    ModalWithOptionalData,
    ModalWithRequiredData,
    ModalWithoutData,
    PbDialogProps,
    TypedDialogInstance,
} from '~/types/modals';
import { ModalComponents } from '~/types/modals';

// Required/optional data enforcement is derived from ModalDataMap (see types/modals.ts) —
// a modal's payload type is the single source of truth for what its callers must pass.
export function useDynamicDialog<T extends ModalWithRequiredData>(name: T, data: ModalDataMap[T], props?: PbDialogProps): DynamicDialogInstance;
export function useDynamicDialog<T extends ModalWithOptionalData>(name: T, data?: ModalDataMap[T], props?: PbDialogProps): DynamicDialogInstance;
export function useDynamicDialog<T extends ModalWithoutData>(name: T, data?: undefined, props?: PbDialogProps): DynamicDialogInstance;
export function useDynamicDialog(name: ModalNames, data?: unknown, props?: PbDialogProps): DynamicDialogInstance {
    const nuxtApp = useNuxtApp();

    const defaultProps: PbDialogProps = {
        modal: true,
        closable: false,
        dismissableMask: false,
        pt: {
            mask: 'bg-[#09090b80] modal-main',
        },
    };

    const dialogOptions: DynamicDialogOptions = {
        props: { ...defaultProps, ...props },
        onClose: props?.onClose,
    };

    if (data !== undefined) {
        dialogOptions.data = data;
    }

    return nuxtApp.$dialog.open(ModalComponents[name as keyof typeof ModalComponents](), dialogOptions);
}

export function getDialogRef<T extends keyof typeof ModalComponents>(_name: T): Ref<TypedDialogInstance<ModalDataForComponent<T>>> {
    return inject<Ref<TypedDialogInstance<ModalDataForComponent<T>>>>('dialogRef')!;
}
